import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Building2, Boxes, MessageSquareQuote, Tv2, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STEPS = [
  {
    n: 1,
    icon: Building2,
    title: "Brand info",
    blurb: "Name your store and pick your colors, fonts and currency.",
    detail: "This is what your customers see on screen — logo, accent color, currency, the works. Two minutes, max.",
    cta: "Start with Brand info",
    to: "/dashboard/brand",
  },
  {
    n: 2,
    icon: Boxes,
    title: "Catalog",
    blurb: "Upload your products — drag in a CSV or add them one by one.",
    detail: "Pricing, colors, sizes, images. Headers like \"Price (INR)\" auto-detect your currency. The image column will be downloaded for you.",
    cta: "Add my products",
    to: "/dashboard/catalog",
  },
  {
    n: 3,
    icon: MessageSquareQuote,
    title: "Questions",
    blurb: "Design 3–7 questions you'd ask a customer in person.",
    detail: "What's the occasion? What's your style? The AI matches their answers to the right product. Tap \"Suggest with AI\" if you're stuck.",
    cta: "Set up Questions",
    to: "/dashboard/questions",
  },
  {
    n: 4,
    icon: Tv2,
    title: "Experiences",
    blurb: "Generate the link and pin it to any device — tablet, phone, or in-store screen.",
    detail: "You'll get a preview link to share with your team, and a public link customers can use once you upgrade.",
    cta: "Create the kiosk link",
    to: "/dashboard/experiences",
  },
];

export default function OnboardingModal() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const viewLogged = useRef(false);

  const shouldShow = !!user && (user.onboarding_views ?? 0) < 4 && !user.onboarding_dismissed;

  useEffect(() => {
    if (!shouldShow) return;
    // Open once per session; the API view counter increments once per modal open.
    setOpen(true);
    if (viewLogged.current) return;
    viewLogged.current = true;
    api.post("/account/onboarding", { action: "view" })
      .then(({ data }) => setUser((u) => (u ? { ...u, ...data } : u)))
      .catch(() => { /* non-fatal */ });
    // We only want this to fire on initial mount when shouldShow becomes true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  const close = () => setOpen(false);

  const dismissForever = async () => {
    try {
      const { data } = await api.post("/account/onboarding", { action: "dismiss" });
      setUser((u) => (u ? { ...u, ...data } : u));
    } catch {/* non-fatal */}
    setOpen(false);
  };

  const goTo = (to) => {
    setOpen(false);
    navigate(to);
  };

  if (!shouldShow) return null;

  const visitsLeft = Math.max(0, 4 - (user.onboarding_views ?? 0));
  const step = STEPS[active];
  const Icon = step.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
      <DialogContent
        className="max-w-3xl p-0 rounded-none border-0 overflow-hidden bg-white"
        data-testid="onboarding-modal"
      >
        {/* Top bar */}
        <div className="relative bg-[#0F1210] text-white px-8 py-7">
          <DialogTitle className="text-2xl tracking-tight font-light leading-tight">
            Welcome to <span className="text-[#10b981] font-medium">Getcurate</span>, {user.brand_name?.split(" ")[0] || "there"}.
          </DialogTitle>
          <p className="text-sm text-white/70 mt-2 max-w-xl">
            Your store goes live in 4 steps. We'll walk you through it — pick a step to jump straight in.
          </p>
        </div>

        <div className="grid md:grid-cols-[260px_1fr] divide-x divide-[#e5e5e5]">
          {/* Stepper rail */}
          <ol className="bg-[#fafafa] p-3">
            {STEPS.map((s, i) => {
              const SI = s.icon;
              const isActive = i === active;
              return (
                <li key={s.n}>
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`onboarding-step-${s.n}`}
                    className={`w-full flex items-start gap-3 px-3 py-3 text-left transition-colors ${
                      isActive ? "bg-white shadow-sm" : "hover:bg-white/60"
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-7 h-7 grid place-items-center text-xs font-medium tracking-tight ${
                        isActive ? "bg-[#0F1210] text-white" : "bg-white text-[#52525b] border border-[#e5e5e5]"
                      }`}
                    >
                      {s.n}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <SI className="w-3.5 h-3.5 text-[#52525b]" />
                        <span className={`text-sm ${isActive ? "text-[#0a0a0a] font-medium" : "text-[#3f3f46]"}`}>
                          {s.title}
                        </span>
                      </span>
                      <span className="block text-xs text-[#71717a] mt-0.5 leading-snug">
                        {s.blurb}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Active step detail */}
          <div className="p-8 flex flex-col min-h-[340px]">
            <div className="label-overline text-[#71717a]">Step {step.n} of 4</div>
            <div className="flex items-center gap-3 mt-2">
              <Icon className="w-6 h-6 text-[#10b981]" />
              <h3 className="text-2xl tracking-tight">{step.title}</h3>
            </div>
            <p className="mt-4 text-[15px] text-[#3f3f46] leading-relaxed">
              {step.detail}
            </p>

            <ul className="mt-6 space-y-2 text-sm text-[#3f3f46]">
              {step.n === 1 && (
                <>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Add a logo, accent color and font.</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Pick the currency (or let the CSV decide).</li>
                </>
              )}
              {step.n === 2 && (
                <>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Click <span className="font-medium">Import CSV</span> at top right.</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Or hit <span className="font-medium">Add product</span> for one at a time.</li>
                </>
              )}
              {step.n === 3 && (
                <>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Add 3 to 7 questions — think "What's the occasion?", "What's your style?".</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Tag each answer (e.g. "casual", "bold") — that's how the AI matches.</li>
                </>
              )}
              {step.n === 4 && (
                <>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Hit <span className="font-medium">Create experience</span>.</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" /> Open the preview link on any device — tablet, phone, or browser — to test it.</li>
                </>
              )}
            </ul>

            <div className="mt-auto pt-8 flex items-center justify-between gap-4">
              <button
                onClick={dismissForever}
                data-testid="onboarding-dismiss-forever"
                className="text-xs text-[#71717a] hover:text-[#0a0a0a] underline-offset-4 hover:underline"
              >
                Don't show again
              </button>
              <div className="flex items-center gap-3">
                {active > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setActive(active - 1)}
                    className="rounded-none border-[#e5e5e5] h-10 px-4 text-sm"
                    data-testid="onboarding-prev"
                  >
                    Back
                  </Button>
                )}
                {active < STEPS.length - 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setActive(active + 1)}
                    className="rounded-none border-[#e5e5e5] h-10 px-4 text-sm"
                    data-testid="onboarding-next"
                  >
                    Next step
                  </Button>
                )}
                <Button
                  onClick={() => goTo(step.to)}
                  data-testid={`onboarding-cta-${step.n}`}
                  className="rounded-none bg-[#0F1210] hover:bg-black text-white h-10 px-5 text-sm flex items-center gap-2"
                >
                  {step.cta} <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="mt-5 text-xs text-[#71717a]">
              This guide will appear {visitsLeft} more time{visitsLeft === 1 ? "" : "s"} on sign-in.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
